import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send session cookie on every request. Required by /api/auth/session
// and every protected backend endpoint that reads the httpOnly cookie.
axios.defaults.withCredentials = true;

export const apiClient = axios.create({ baseURL: API, withCredentials: true });

// -------------------------------------------------------------------------
// Bearer-token request interceptor.
//
// Mirrors the interceptor installed by AuthContext.jsx so `apiClient`
// callers (RecordSaleModal, admin utilities, etc.) authenticate even
// when the cookie path is unavailable — notably the embedded preview
// iframe, which relies on the token seeded into localStorage under
// `cdi.auth.token`. Existing per-request `Authorization` headers are
// respected (never overwritten).
// -------------------------------------------------------------------------
const AUTH_TOKEN_KEY = "cdi.auth.token";
apiClient.interceptors.request.use((config) => {
  try {
    if (typeof window !== "undefined") {
      const tok = window.localStorage.getItem(AUTH_TOKEN_KEY);
      if (tok) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${tok}`;
        }
      }
    }
  } catch { /* localStorage may throw in private-mode iframes — noop */ }
  return config;
});

// -------------------------------------------------------------------------
// Global 401 handler.
//
// Any protected request that returns 401 means the JWT is expired / the
// session was revoked / the idle-timeout tripped. Redirect the user to
// /auth/session-expired so RequireAuth doesn't loop them through the
// login screen with no context. We skip the redirect for /api/auth/*
// requests (login / signup / session probe) — those handle their own
// error UI, including the AuthContext mount-time `/auth/session` probe
// which treats a 401 as the normal signed-out state.
// -------------------------------------------------------------------------
const _isAuthEndpoint = (url = "") => {
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url;
    // Accept BOTH forms:
    //   • Absolute (fetch / full URL):   /api/auth/session
    //   • Relative (apiClient baseURL):  /auth/session
    // The apiClient instance has `baseURL: /api`, so request configs
    // carry the relative form. Before this fix, a cold-load /session
    // probe fell through and triggered the generic session-expired
    // redirect for a never-logged-in visitor.
    return path.startsWith("/auth/") || path.includes("/api/auth/");
  } catch { return false; }
};

const _install401Handler = (client) => {
  client.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      if (status === 401 && !_isAuthEndpoint(url) && typeof window !== "undefined") {
        // Trial expiry is a distinct code returned by the backend
        // `_resolve_session` gate — send those users to the dedicated
        // trial-expired screen instead of the generic session-expired
        // screen so they get pricing/next-steps CTAs.
        const detail = err?.response?.data?.detail;
        const isTrial = detail && typeof detail === "object" && detail.code === "TRIAL_EXPIRED";
        const here = window.location.pathname + window.location.search;
        if (isTrial) {
          if (!here.startsWith("/auth/trial-expired") && !here.startsWith("/auth/purchase-basic")) {
            // 2026-02-26 — client-side navigation preserves the
            // React tree (AuthProvider, session state, tier) so the
            // Trial Expired screen inherits the user profile fetched
            // by `/session` and can hand the caller's email to
            // Stripe. A hard `window.location.replace` would reload
            // the SPA and lose that state on the first hop.
            if (typeof window.__cdiNavigate === "function") {
              window.__cdiNavigate("/auth/trial-expired");
            } else {
              // First-load fallback before the NavigationBridge in
              // App.js has mounted.
              window.location.replace("/auth/trial-expired");
            }
          }
        } else {
          // Only treat this 401 as an expired session if the user
          // had successfully authenticated at some point in this
          // page lifecycle. `window.__cdiWasAuthenticated` is set
          // in AuthContext the first time any auth flow (login,
          // magic-link, cold-load /session with a valid user)
          // resolves with a real user, and cleared on explicit
          // logout. Without this flag a cold-load 401 for a never-
          // logged-in visitor would bounce them straight to
          // /auth/session-expired instead of /auth/login.
          if (!window.__cdiWasAuthenticated) {
            return Promise.reject(err);
          }
          // Re-probe /api/auth/session before kicking the user out.
          // A 401 on one request can be transient (clock drift during
          // a token refresh, a stray revoked-sibling tab) — don't blow
          // away the login unless the probe also comes back without
          // a user. On confirmed dead session, redirect via the
          // in-tree navigator so AuthProvider / React state survives.
          //
          // De-dup: a dashboard that fans out 5+ API calls in parallel
          // used to spawn 5+ /session probes on auth lapse. We now
          // cache the probe PROMISE on `window.__sessionProbeInFlight`
          // — the first 401 kicks off the probe, every late-arriving
          // 401 awaits the SAME promise and shares its verdict instead
          // of racing to fire its own. Boolean falsy state is restored
          // in the inner `finally` so a fresh wave of 401s after the
          // first verdict can start a new probe cycle.
          //
          // We never auto-clear `cdi.auth.token` on a 401 anymore;
          // explicit logout (AuthContext.logout → _setToken(null)) is
          // the sole clearing path, so a flaky request can't log the
          // user out from under themselves.
          (async () => {
            if (!window.__sessionProbeInFlight) {
              window.__sessionProbeInFlight = (async () => {
                try {
                  const probe = await axios.get(`${API}/auth/session`, { withCredentials: true });
                  return !!(probe?.data?.user);
                } catch {
                  return false; // probe failed → treat session as dead
                } finally {
                  window.__sessionProbeInFlight = false;
                }
              })();
            }
            const userStillAlive = await window.__sessionProbeInFlight;
            if (userStillAlive) return;
            if (window.location.pathname.startsWith("/auth/")) return;
            if (typeof window.__cdiNavigate === "function") {
              window.__cdiNavigate("/auth/session-expired");
            } else {
              // First-load fallback before the NavigationBridge in
              // App.js has registered __cdiNavigate.
              window.location.replace("/auth/session-expired");
            }
          })();
        }
      }
      return Promise.reject(err);
    }
  );
};
_install401Handler(axios);
_install401Handler(apiClient);

// Legacy admin-token injection removed (security: the shared-secret
// `X-CDI-Admin` header + `REACT_APP_CDI_ADMIN_TOKEN` are gone). Admin
// endpoints are now guarded on the backend by `require_admin_session`
// which resolves the caller's login cookie — no client-side header
// is required or accepted.

// -------------------------------------------------------------------------
// Admin "Last Used" tracker.
//
// Mirrors the backend's ADMIN_ROUTES list. Every successful (2xx) response
// from an admin endpoint stamps ``{ "METHOD::/api/...template": ISO }``
// into localStorage under ``ADMIN_LAST_USED_KEY``. The Settings > Admin
// Health panel reads this map to render a "Last used" column beside each
// endpoint row — pure UI signal, no backend involvement.
// -------------------------------------------------------------------------
export const ADMIN_LAST_USED_KEY = "cdi.admin.lastUsed";

// Path templates carry ``{name}`` placeholders identical to the backend
// route decorators so keys match 1:1 with the ADMIN_ROUTES the health
// endpoint reports.
const ADMIN_ROUTE_TEMPLATES = [
  { method: "POST", template: "/api/admin/reseed-master-templates" },
  { method: "GET",  template: "/api/categories/{cat_id}/rare-values" },
  { method: "POST", template: "/api/categories/{cat_id}/rare-values/merge" },
  { method: "POST", template: "/api/categories/{cat_id}/rare-values/delete" },
  { method: "GET",  template: "/api/rare-values/all-categories" },
  { method: "POST", template: "/api/items/migrate-legacy" },
];

// Precompute a matcher (regex + template) per admin route so runtime
// matching is a cheap loop.
const ADMIN_ROUTE_MATCHERS = ADMIN_ROUTE_TEMPLATES.map((r) => ({
  method: r.method,
  template: r.template,
  regex: new RegExp(
    "^" + r.template.replace(/\{[^}]+\}/g, "[^/]+").replace(/\//g, "\\/") + "$"
  ),
}));

const _matchAdminRoute = (method, fullPath) => {
  const m = (method || "GET").toUpperCase();
  for (const r of ADMIN_ROUTE_MATCHERS) {
    if (r.method === m && r.regex.test(fullPath)) return r;
  }
  return null;
};

export const getAdminLastUsedMap = () => {
  try {
    const raw = localStorage.getItem(ADMIN_LAST_USED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const _stampAdminLastUsed = (method, template) => {
  try {
    const map = getAdminLastUsedMap();
    map[`${method}::${template}`] = new Date().toISOString();
    localStorage.setItem(ADMIN_LAST_USED_KEY, JSON.stringify(map));
  } catch { /* private mode / quota — swallow */ }
};

apiClient.interceptors.response.use((response) => {
  try {
    const cfg = response.config || {};
    const status = response.status || 0;
    if (status >= 200 && status < 300) {
      // ``cfg.url`` may be relative (baseURL prepended by axios) — normalise
      // to the ``/api/...`` prefix the templates use.
      let url = cfg.url || "";
      if (url.startsWith("http")) {
        try { url = new URL(url).pathname; } catch { /* leave as-is */ }
      }
      if (!url.startsWith("/api/")) {
        url = "/api" + (url.startsWith("/") ? url : `/${url}`);
      }
      // Strip query string if present.
      const q = url.indexOf("?");
      if (q >= 0) url = url.slice(0, q);
      const match = _matchAdminRoute(cfg.method, url);
      if (match) _stampAdminLastUsed(match.method, match.template);
    }
  } catch { /* never let tracking break a real request */ }
  return response;
});

export const formatNZD = (v) => {
  const n = Number(v || 0);
  return `$${n.toFixed(2)} NZD`;
};

// Collection-aware formatter
export const formatMoney = (v, collection) => {
  const n = Number(v || 0);
  const sym = (collection && collection.currency_symbol) || "$";
  const code = (collection && collection.currency) || "NZD";
  return `${sym}${n.toFixed(2)} ${code}`;
};
